import { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from '../components/Modal'
import { useAuth } from '../auth/AuthProvider'
import { agruparExcecoes, rowsInsertExcecao } from '../lib/calendarioModalidade'
import { MODALIDADE_GRUPOS_ATIVOS, type ModalidadeGrupoId } from '../lib/modalidadeGrupos'
import { supabase } from '../lib/supabase'
import type { CalendarioDia, TipoDia } from '../types/database'

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

export function CalendarioPage() {
  const { canWrite } = useAuth()
  const [cursor, setCursor] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [dias, setDias] = useState<CalendarioDia[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exModalOpen, setExModalOpen] = useState(false)
  const [exForm, setExForm] = useState({ data: '', grupoId: 'infantil' as ModalidadeGrupoId, motivo: '' })
  const writable = canWrite('calendario')

  const year = cursor.getFullYear()
  const month = cursor.getMonth()

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const start = `${year}-${String(month + 1).padStart(2, '0')}-01`
    const endDate = new Date(year, month + 1, 0)
    const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
    const calRes = await supabase.from('calendario').select('*').gte('data', start).lte('data', end)
    if (calRes.error) setError(calRes.error.message)
    else setDias((calRes.data as CalendarioDia[]) || [])
    setLoading(false)
  }, [year, month])

  useEffect(() => {
    document.title = 'Calendário · ISEN EDUCAE'
    load()
  }, [load])

  const map = useMemo(() => new Map(dias.map((d) => [d.data, d])), [dias])

  const cells = useMemo(() => {
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const firstDowFixed = new Date(year, month, 1).getDay()
    const result: { day: number | null; iso?: string; tipo?: TipoDia; dia?: CalendarioDia }[] = []
    for (let i = 0; i < firstDowFixed; i++) result.push({ day: null })
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      result.push({ day: d, iso, tipo: map.get(iso)?.tipo, dia: map.get(iso) })
    }
    return result
  }, [year, month, map])

  const feriados = dias.filter((d) => d.tipo === 'feriado')
  const letivos = dias.filter((d) => d.tipo === 'letivo')

  const excecoesRaw = useMemo(() => {
    const start = `${year}-${String(month + 1).padStart(2, '0')}-01`
    const endDate = new Date(year, month + 1, 0)
    const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
    return { start, end }
  }, [year, month])

  const [excecoesMes, setExcecoesMes] = useState<ReturnType<typeof agruparExcecoes>>([])

  const loadExcecoes = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('calendario_excecoes')
      .select('*')
      .gte('data', excecoesRaw.start)
      .lte('data', excecoesRaw.end)
    if (!err) setExcecoesMes(agruparExcecoes(data || []))
  }, [excecoesRaw.end, excecoesRaw.start])

  useEffect(() => {
    void loadExcecoes()
  }, [loadExcecoes])

  async function toggle(iso: string) {
    if (!writable) return
    setError(null)
    const current = map.get(iso)
    if (!current) {
      const { error: err } = await supabase.from('calendario').insert({ data: iso, tipo: 'letivo' })
      if (err) setError(err.message)
    } else if (current.tipo === 'letivo') {
      const { error: err } = await supabase.from('calendario').update({ tipo: 'feriado' }).eq('id', current.id)
      if (err) setError(err.message)
    } else {
      const { error: err } = await supabase.from('calendario').delete().eq('id', current.id)
      if (err) setError(err.message)
    }
    load()
  }

  async function marcarUteis() {
    if (!writable) return
    setError(null)
    const { error: err } = await supabase.rpc('marcar_uteis_mes', {
      p_ano: year,
      p_mes: month + 1,
    })
    if (err) setError(err.message)
    else load()
  }

  async function removeFeriado(id: string) {
    const { error: err } = await supabase.from('calendario').delete().eq('id', id)
    if (err) setError(err.message)
    else load()
  }

  async function addExcecao() {
    if (!writable || !exForm.data) return
    setError(null)
    const rows = rowsInsertExcecao(exForm.data, exForm.grupoId, exForm.motivo.trim() || null)
    const { error: err } = await supabase.from('calendario_excecoes').upsert(rows, {
      onConflict: 'data,modalidade',
      ignoreDuplicates: false,
    })
    if (err) {
      setError(err.message)
      return
    }
    setExForm({ data: '', grupoId: 'infantil', motivo: '' })
    await loadExcecoes()
  }

  async function removeExcecao(ids: string[]) {
    const { error: err } = await supabase.from('calendario_excecoes').delete().in('id', ids)
    if (err) setError(err.message)
    else await loadExcecoes()
  }

  function cellTitle(dia?: CalendarioDia) {
    if (!dia) return undefined
    const parts: string[] = []
    if (dia.tipo === 'letivo') parts.push('Dia letivo')
    if (dia.tipo === 'feriado') parts.push('Feriado / não letivo')
    if (dia.trimestre != null) parts.push(`Trimestre ${dia.trimestre}`)
    if (dia.semana_cardapio != null) parts.push(`Semana cardápio ${dia.semana_cardapio}`)
    return parts.length ? parts.join(' · ') : undefined
  }

  const title = cursor.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })

  return (
    <div className="calendar-page">
      <div className="page-header calendar-page-header">
        <h1>Calendário letivo</h1>
        <div className="page-actions">
          {writable && (
            <>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => void marcarUteis()}>
                Marcar dias úteis do mês
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExModalOpen(true)}>
                Exceções por modalidade
              </button>
            </>
          )}
        </div>
      </div>
      {error && <div className="alert alert-error calendar-page-alert">{error}</div>}
      {loading ? (
        <div className="loading">Carregando…</div>
      ) : (
        <>
          <div className="calendar-legend">
            <div className="calendar-legend-section">
              <span className="calendar-legend-heading">Cores</span>
              <ul className="calendar-legend-list">
                <li>
                  <span className="calendar-legend-swatch" aria-hidden />
                  Branco — sem registro (sem merenda)
                </li>
                <li>
                  <span className="calendar-legend-swatch letivo" aria-hidden />
                  Verde — dia letivo (conta no consumo)
                </li>
                <li>
                  <span className="calendar-legend-swatch feriado" aria-hidden />
                  Vermelho — feriado / não letivo
                </li>
              </ul>
            </div>
            <div className="calendar-legend-section">
              <span className="calendar-legend-heading">Tags na célula</span>
              <ul className="calendar-legend-list">
                <li>
                  <span className="calendar-legend-tag">L</span> Letivo
                </li>
                <li>
                  <span className="calendar-legend-tag">F</span> Feriado
                </li>
                <li>
                  <span className="calendar-legend-tag">T1</span> Trimestre do cardápio (1–4)
                </li>
                <li>
                  <span className="calendar-legend-tag">S3</span> Semana do cardápio (1–13)
                </li>
              </ul>
            </div>
            <div className="calendar-legend-section calendar-legend-meta">
              {writable && (
                <p className="calendar-legend-hint">Clique na data: vazio → letivo → feriado → limpar</p>
              )}
              <p className="calendar-legend-hint">
                Sem dias letivos o consumo e o pedido ficam zerados. T/S são calculados automaticamente.
              </p>
            </div>
          </div>

          <div className="calendar-layout">
            <div className="calendar-grid-card">
              <div className="calendar-toolbar">
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={() => setCursor(new Date(year, month - 1, 1))}
                  aria-label="Mês anterior"
                >
                  ◀
                </button>
                <strong className="calendar-month-title">{title}</strong>
                <button
                  type="button"
                  className="calendar-nav-btn"
                  onClick={() => setCursor(new Date(year, month + 1, 1))}
                  aria-label="Próximo mês"
                >
                  ▶
                </button>
              </div>
              <div className="calendar-weekdays">
                {WEEKDAYS.map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="calendar-grid">
                {cells.map((c, i) =>
                  c.day == null ? (
                    <div key={i} className="calendar-cell empty" />
                  ) : (
                    <button
                      key={c.iso}
                      type="button"
                      className={`calendar-cell ${c.tipo || ''}`}
                      title={cellTitle(c.dia)}
                      onClick={() => c.iso && void toggle(c.iso!)}
                      disabled={!writable}
                    >
                      <strong>{c.day}</strong>
                      {c.tipo && <span className="cell-tag">{c.tipo === 'letivo' ? 'L' : 'F'}</span>}
                      {c.dia?.trimestre != null && (
                        <span className="cell-tag">T{c.dia.trimestre}</span>
                      )}
                      {c.dia?.semana_cardapio != null && (
                        <span className="cell-tag">S{c.dia.semana_cardapio}</span>
                      )}
                    </button>
                  ),
                )}
              </div>
            </div>
            <div className="calendar-side-panel">
              <div className="calendar-side-kpis">
                <div className="calendar-side-kpi">
                  <div className="calendar-side-kpi-label">Letivos</div>
                  <div className="calendar-side-kpi-value">{letivos.length}</div>
                </div>
                <div className="calendar-side-kpi">
                  <div className="calendar-side-kpi-label">Feriados</div>
                  <div className="calendar-side-kpi-value">{feriados.length}</div>
                </div>
              </div>
              <div className="calendar-side-scroll">
                <h3 className="calendar-side-title">Feriados</h3>
                <ul className="calendar-side-list">
                  {feriados.map((f) => (
                    <li key={f.id}>
                      {f.data.slice(8, 10)}/{f.data.slice(5, 7)}
                      {writable && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => void removeFeriado(f.id)}
                        >
                          Remover
                        </button>
                      )}
                    </li>
                  ))}
                  {!feriados.length && <li className="kpi-sub">Nenhum neste mês</li>}
                </ul>
                {excecoesMes.length > 0 && (
                  <>
                    <h3 className="calendar-side-title">Exceções no mês</h3>
                    <ul className="calendar-side-list">
                      {excecoesMes.map((e) => (
                        <li key={e.key}>
                          {e.data.slice(8, 10)}/{e.data.slice(5, 7)} — {e.label}
                          {e.motivo ? `: ${e.motivo}` : ''}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <Modal
        open={exModalOpen}
        title="Exceções por modalidade"
        onClose={() => setExModalOpen(false)}
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setExModalOpen(false)}>
            Fechar
          </button>
        }
      >
        <p className="kpi-sub" style={{ marginBottom: 12 }}>
          Dias em que uma modalidade não segue o cardápio (excluída do consumo previsto).
          Infantil aplica a creche e pré.
        </p>
        {writable && (
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div className="field">
              <label>Data</label>
              <input
                type="date"
                value={exForm.data}
                onChange={(e) => setExForm({ ...exForm, data: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Modalidade</label>
              <select
                value={exForm.grupoId}
                onChange={(e) =>
                  setExForm({ ...exForm, grupoId: e.target.value as ModalidadeGrupoId })
                }
              >
                {MODALIDADE_GRUPOS_ATIVOS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Motivo</label>
              <input
                value={exForm.motivo}
                onChange={(e) => setExForm({ ...exForm, motivo: e.target.value })}
                placeholder="Opcional"
              />
            </div>
            <button type="button" className="btn btn-primary" onClick={() => void addExcecao()}>
              Adicionar
            </button>
          </div>
        )}
        <table className="data-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Modalidade</th>
              <th>Motivo</th>
              {writable && <th />}
            </tr>
          </thead>
          <tbody>
            {excecoesMes.map((e) => (
              <tr key={e.key}>
                <td>{e.data}</td>
                <td>{e.label}</td>
                <td>{e.motivo || '—'}</td>
                {writable && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void removeExcecao(e.ids)}
                    >
                      Remover
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {!excecoesMes.length && (
              <tr>
                <td colSpan={writable ? 4 : 3} className="kpi-sub">
                  Nenhuma exceção cadastrada neste mês.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Modal>
    </div>
  )
}